import { createSlice } from '@reduxjs/toolkit';
import axios from '../../services/authService';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { parseApiResponse } from '../../utilities/parseApiResponse';

export const createTestEventSlice = createSlice({
  name: 'createTestEvent',
  initialState: {
    loading: false,
    progress: null, // 'event' | 'scenarios' | 'attributes' | 'complete'
    eventId: null,
    scenarioIds: [],
    response: {},
    error: null,
  },
  reducers: {
    reset: (state) => {
      state.loading = false;
      state.progress = null;
      state.eventId = null;
      state.scenarioIds = [];
      state.response = {};
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setProgress: (state, action) => {
      state.progress = action.payload;
    },
    setEventId: (state, action) => {
      state.eventId = action.payload;
    },
    setScenarioIds: (state, action) => {
      state.scenarioIds = action.payload;
    },
    setResponse: (state, action) => {
      state.response = action.payload;
      state.loading = false;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
});

export const { reset, setLoading, setProgress, setEventId, setScenarioIds, setResponse, setError } =
  createTestEventSlice.actions;

// Function to create attributes on existing scenarios
export const createAttributesOnExistingScenariosAsync = (attributesData) => async (dispatch) => {
  try {
    const inboundAttrs = [];
    const outboundAttrs = [];

    attributesData.forEach((attr) => {
      const payload = {
        name: attr.attribute_name || attr.name,
        expected: attr.expected_value || attr.expectedValue, // Expected value goes to 'expected' field
        config_type: attr.attribute_type || attr.type || 'Static', // Static/Dynamic goes to 'config_type' field
      };

      if (attr.scenario_type === 'inbound') {
        inboundAttrs.push({
          section_data: 'test-event-inbound-details',
          test_events_inbound_id: attr.scenario_db_id,
          ...payload,
        });
      } else {
        outboundAttrs.push({
          section_data: 'test-event-outbound-details',
          test_events_outbound_id: attr.scenario_db_id,
          ...payload,
        });
      }
    });

    const result = {
      inboundIds: [],
      outboundIds: [],
    };

    if (inboundAttrs.length > 0) {
      const response = await axios.post(API_ENDPOINTS.INSERT_TEST_FLOW, inboundAttrs);
      const parsedBody = parseApiResponse(response);
      result.inboundIds = parsedBody?.resDataObject?.['test_event_inbound_details'] || [];
    }

    if (outboundAttrs.length > 0) {
      const response = await axios.post(API_ENDPOINTS.INSERT_TEST_FLOW, outboundAttrs);
      const parsedBody = parseApiResponse(response);
      result.outboundIds = parsedBody?.resDataObject?.['test_event_outbound_details'] || [];
    }

    return { success: true, attributeIds: result };
  } catch (error) {
    console.error('Error creating attributes on existing scenarios:', error);
    return { success: false, error: error.message };
  }
};

// Main orchestration function
export const createTestEventWithScenariosAsync =
  (flowId, eventData, scenarios, attributes, existingEventId = null) =>
  async (dispatch, getState) => {
    dispatch(reset());
    dispatch(setLoading(true));

    try {
      let eventId = existingEventId;

      // Step 1: Create the event (only if it doesn't exist)
      if (!existingEventId) {
        dispatch(setProgress('event'));
        const eventPayload = [
          {
            section_data: 'test-events',
            test_flow_id: flowId,
            event_name: eventData.name,
            description: eventData.description || '',
            sequence: eventData.order || 1,
          },
        ];

        const eventResponse = await axios.post(API_ENDPOINTS.INSERT_TEST_FLOW, eventPayload);

        const parsedBody = parseApiResponse(eventResponse);
        if (parsedBody?.resDataObject?.test_events?.length > 0) {
          eventId = parsedBody.resDataObject.test_events[0];
        }

        if (!eventId) {
          console.error('API did not return an event ID');
          throw new Error('Failed to get event ID from API response');
        }

        dispatch(setEventId(eventId));
      } else {
      }

      // Step 2: Create scenarios (separated by type)
      dispatch(setProgress('scenarios'));

      // Separate inbound and outbound scenarios
      const inboundScenarios = scenarios.filter((e) => e.type === 'inbound');
      const outboundScenarios = scenarios.filter((e) => e.type !== 'inbound');

      let scenarioIds = [];

      // Create inbound scenarios
      if (inboundScenarios.length > 0) {
        const inboundPayload = inboundScenarios.map((scenario) => ({
          section_data: 'test-events-inbound',
          test_event_id: eventId,
          scenario_name: scenario.name,
          scenario_type: 'inbound',
          description: scenario.description || '',
          matching_attribute_name: scenario.matching_attribute_name || null,
          matching_attribute_value: scenario.matching_attribute_value || null,
        }));

        const inboundResponse = await axios.post(API_ENDPOINTS.INSERT_TEST_FLOW, inboundPayload);

        const parsedBody = parseApiResponse(inboundResponse);
        const inboundIds =
          parsedBody?.resDataObject?.['test-events-inbound'] ||
          parsedBody?.resDataObject?.test_events_inbound;
        if (inboundIds?.length > 0) {
          inboundIds.forEach((id) => {
            scenarioIds.push({ id: id, type: 'inbound' });
          });
        }
      }

      // Create outbound scenarios
      if (outboundScenarios.length > 0) {
        // Map scenario types to their corresponding outbound_type_id
        const getOutboundTypeId = (scenarioType) => {
          switch (scenarioType.toLowerCase()) {
            case 'mixpanel':
              return 1;
            case 'kochava':
              return 2;
            case 'epsilon':
              return 3;
            default:
              console.warn(`Unknown scenario type: ${scenarioType}, defaulting to 1`);
              return 1;
          }
        };

        const outboundPayload = outboundScenarios.map((scenario) => {
          const outboundTypeId = getOutboundTypeId(scenario.type);

          return {
            section_data: 'test-events-outbound',
            test_event_id: eventId,
            scenario_name: scenario.name,
            scenario_type: scenario.type, // Keep the specific outbound type
            outbound_type_id: outboundTypeId,
            description: scenario.description || '',
            matching_attribute_name: scenario.matching_attribute_name || null,
            matching_attribute_value: scenario.matching_attribute_value || null,
          };
        });

        const outboundResponse = await axios.post(API_ENDPOINTS.INSERT_TEST_FLOW, outboundPayload);

        const parsedBody = parseApiResponse(outboundResponse);
        const outboundIds =
          parsedBody?.resDataObject?.['test-events-outbound'] ||
          parsedBody?.resDataObject?.test_events_outbound;
        if (outboundIds?.length > 0) {
          outboundIds.forEach((id) => {
            scenarioIds.push({ id: id, type: 'outbound' });
          });
        }
      }

      if (!scenarioIds.length && scenarios.length > 0) {
        console.error('No scenario IDs returned from API');
        throw new Error('Failed to get scenario IDs from API response');
      }

      dispatch(setScenarioIds(scenarioIds));

      // Step 3: Create all attributes at once
      dispatch(setProgress('attributes'));

      // Separate attributes for inbound vs outbound scenarios
      const inboundAttributes = [];
      const outboundAttributes = [];

      attributes.forEach((attr) => {
        const scenarioInfo = scenarioIds[attr.scenarioIndex];
        if (scenarioInfo && scenarioInfo.type === 'inbound') {
          inboundAttributes.push({
            section_data: 'test-event-inbound-details',
            test_events_inbound_id: scenarioInfo.id, // Using the actual ID from API
            name: attr.name,
            expected: attr.expectedValue, // Expected value goes to 'expected' field
            config_type: attr.type, // Static/Dynamic goes to 'config_type' field
          });
        } else if (scenarioInfo) {
          outboundAttributes.push({
            section_data: 'test-event-outbound-details',
            test_events_outbound_id: scenarioInfo.id, // Using the actual ID from API
            name: attr.name,
            expected: attr.expectedValue, // Expected value goes to 'expected' field
            config_type: attr.type, // Static/Dynamic goes to 'config_type' field
          });
        }
      });

      // Send inbound attributes if any
      let inboundAttributeIds = [];
      if (inboundAttributes.length > 0) {
        const inboundAttrResponse = await axios.post(
          API_ENDPOINTS.INSERT_TEST_FLOW,
          inboundAttributes,
        );

        const parsedBody = parseApiResponse(inboundAttrResponse);
        if (parsedBody?.resDataObject?.test_event_inbound_details?.length > 0) {
          inboundAttributeIds = parsedBody.resDataObject.test_event_inbound_details;
        }
      }

      // Send outbound attributes if any
      let outboundAttributeIds = [];
      if (outboundAttributes.length > 0) {
        const outboundAttrResponse = await axios.post(
          API_ENDPOINTS.INSERT_TEST_FLOW,
          outboundAttributes,
        );

        const parsedBody = parseApiResponse(outboundAttrResponse);
        if (parsedBody?.resDataObject?.test_event_outbound_details?.length > 0) {
          outboundAttributeIds = parsedBody.resDataObject.test_event_outbound_details;
        }
      }

      // Mark as complete
      dispatch(setProgress('complete'));
      dispatch(
        setResponse({
          eventId,
          scenarioIds,
          attributeIds: {
            inbound: inboundAttributeIds,
            outbound: outboundAttributeIds,
          },
          message: 'Event, scenarios, and attributes created successfully',
        }),
      );

      return {
        success: true,
        eventId,
        scenarioIds,
        attributeIds: {
          inbound: inboundAttributeIds,
          outbound: outboundAttributeIds,
        },
      };
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create event';
      console.error('Error in event creation process:', errorMessage);
      dispatch(setError(errorMessage));

      // Get current progress from the store to know where it failed
      const currentProgress = getState().createTestEvent.progress;
      return {
        success: false,
        error: errorMessage,
        progress: currentProgress, // Return where it failed
      };
    }
  };

// Selectors
export const selectCreateTestEventLoading = (state) => state.createTestEvent.loading;
export const selectCreateTestEventProgress = (state) => state.createTestEvent.progress;
export const selectCreateTestEventResponse = (state) => state.createTestEvent.response;
export const selectCreateTestEventError = (state) => state.createTestEvent.error;

export default createTestEventSlice.reducer;
